/**
 * DocumentReviewer — admin-side per-driver document review.
 * Used inline inside ApprovalsPanel rows so a reviewer can approve the
 * driver in the same pass as approving each compliance document.
 *
 * Strict isolation contract:
 *   - Pure UI surface; no dispatch / state-machine / janitor /
 *     offer-routing / auth code touched.
 *   - Fetches per-doc metadata from /admin/drivers/{id}/documents.
 *   - Fetches the raw base64 blob on demand via
 *     /admin/documents/{id}/preview so the list response stays small.
 */
import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { CheckCircle2, XCircle, FileText, Eye, Loader2, CalendarClock, Upload } from "lucide-react";

const DOC_LABELS = {
  licence:   "Driver's Licence",
  badge:     "Private Hire Driver Badge",
  insurance: "Hire & Reward Insurance",
  vehicle_licence: "Private Hire Vehicle Licence",
  vehicle_logbook: "Vehicle Log Book (V5C)",
  photo:     "Driver Photo",
  right_to_work: "Right to Work",
  dbs:       "DBS Check",
  mot:       "MOT Certificate",
};
const DOC_ORDER = ["licence", "badge", "right_to_work", "dbs", "photo", "mot", "insurance", "vehicle_licence", "vehicle_logbook"];
// Document types that carry an expiry date. Photo (identity shot) and the V5C
// logbook (never expires) are excluded.
const EXPIRING_TYPES = ["licence", "badge", "insurance", "right_to_work", "dbs", "mot", "vehicle_licence"];

// Client-side mirror of services/compliance.doc_status for inline display.
const expiryStatus = (iso) => {
  if (!iso) return { status: "unset", days: null };
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const exp = new Date(`${iso}T00:00:00`);
  if (isNaN(exp.getTime())) return { status: "unset", days: null };
  const days = Math.round((exp - today) / 86400000);
  if (days < 0) return { status: "expired", days };
  if (days <= 30) return { status: "expiring", days };
  return { status: "valid", days };
};
const EXPIRY_STYLE = {
  expired:  { fg: "#991B1B", bg: "#FEE2E2", label: "EXPIRED" },
  expiring: { fg: "#92400E", bg: "#FEF3C7", label: "EXPIRING" },
  valid:    { fg: "#065F46", bg: "#D1FAE5", label: "VALID" },
  unset:    { fg: "#374151", bg: "#F3F4F6", label: "NO EXPIRY" },
};
const STATUS_BADGE = {
  approved: { bg: "#ECFDF5", border: "#10B981", text: "#065F46", label: "APPROVED" },
  pending:  { bg: "#FFFBEB", border: "#F59E0B", text: "#92400E", label: "AWAITING REVIEW" },
  rejected: { bg: "#FEF2F2", border: "#EF4444", text: "#991B1B", label: "REJECTED" },
  missing:  { bg: "#F9FAFB", border: "#E5E7EB", text: "#6B7280", label: "NOT UPLOADED" },
};

export const DocumentReviewer = ({ driverId, onDocChange, allowUpload = false }) => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [uploadingType, setUploadingType] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  // Preview state — only one preview open at a time to keep memory low
  // when the base64 blobs are large.
  const [previewing, setPreviewing] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  // Expiry date editing — one in-flight edit map keyed by doc id.
  const [expiryDrafts, setExpiryDrafts] = useState({});
  // DBS records an issue date alongside its renewal/expiry date.
  const [issueDrafts, setIssueDrafts] = useState({});
  const [savingExpiryId, setSavingExpiryId] = useState(null);
  // P4 — driver approval readiness (profile / vehicle / docs / bank + verdict).
  const [readiness, setReadiness] = useState(null);

  const fetchDocs = useCallback(async () => {
    if (!driverId) return;
    setLoading(true);
    try {
      const res = await api.get(`/admin/drivers/${driverId}/documents`);
      setDocs(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to load documents");
    } finally {
      setLoading(false);
    }
    try {
      const r = await api.get(`/admin/drivers/${driverId}/readiness`);
      setReadiness(r.data || null);
    } catch {
      setReadiness(null);
    }
  }, [driverId]);
  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const openPreview = async (doc) => {
    if (!doc.id) return;
    setPreviewing(doc.id);
    setPreviewLoading(true);
    setPreviewData(null);
    try {
      const res = await api.get(`/admin/documents/${doc.id}/preview`);
      setPreviewData(res.data);
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to load preview");
      setPreviewing(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewing(null);
    setPreviewData(null);
  };

  const handleApprove = async (doc) => {
    if (busyId || !doc.id) return;
    setBusyId(doc.id);
    try {
      await api.post(`/admin/documents/${doc.id}/approve`);
      toast.success(`${DOC_LABELS[doc.doc_type]} approved`);
      fetchDocs();
      onDocChange?.();
    } catch (e) {
      toast.error(formatApiError(e) || "Approval failed");
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async (doc) => {    if (!doc.id) return;
    if (rejectReason.trim().length < 2) {
      toast.error("Please enter a reason.");
      return;
    }
    setBusyId(doc.id);
    try {
      await api.post(`/admin/documents/${doc.id}/reject`, { reason: rejectReason.trim() });
      toast.success(`${DOC_LABELS[doc.doc_type]} rejected — driver will be notified.`);
      setRejectingId(null);
      setRejectReason("");
      fetchDocs();
      onDocChange?.();
    } catch (e) {
      toast.error(formatApiError(e) || "Rejection failed");
    } finally {
      setBusyId(null);
    }
  };

  const ordered = DOC_ORDER.map((t) => docs.find((d) => d.doc_type === t) || { doc_type: t, status: "missing" });

  // Upload / replace a document ON BEHALF OF the driver (admin endpoint).
  const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",")[1] || "");
    r.onerror = reject;
    r.readAsDataURL(file);
  });

  const handleUpload = async (docType, file) => {
    if (!file || !driverId) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("File too large (max 8MB)"); return; }
    setUploadingType(docType);
    try {
      const data = await fileToBase64(file);
      await api.post(`/admin/drivers/${driverId}/documents`, {
        doc_type: docType,
        filename: file.name,
        content_type: file.type || "application/octet-stream",
        data,
      });
      toast.success(`${DOC_LABELS[docType]} uploaded — awaiting review`);
      fetchDocs();
      onDocChange?.();
    } catch (e) {
      toast.error(formatApiError(e) || "Upload failed");
    } finally {
      setUploadingType(null);
    }
  };

  const saveExpiry = async (doc) => {
    if (!doc.id) return;
    const draft = expiryDrafts[doc.id] ?? doc.expiry_date ?? "";
    setSavingExpiryId(doc.id);
    try {
      const body = { expiry_date: draft || null };
      // DBS also carries an issue date — send it so the backend stores it.
      if (doc.doc_type === "dbs") {
        body.issue_date = (issueDrafts[doc.id] ?? doc.issue_date ?? "") || null;
      }
      await api.post(`/admin/documents/${doc.id}/expiry`, body);
      toast.success(draft ? `${DOC_LABELS[doc.doc_type]} expiry saved` : `${DOC_LABELS[doc.doc_type]} expiry cleared`);
      setExpiryDrafts((s) => { const n = { ...s }; delete n[doc.id]; return n; });
      setIssueDrafts((s) => { const n = { ...s }; delete n[doc.id]; return n; });
      fetchDocs();
      onDocChange?.();
    } catch (e) {
      toast.error(formatApiError(e) || "Couldn't save expiry date");
    } finally {
      setSavingExpiryId(null);
    }
  };

  const renderCard = (doc, tid, nested = false) => {
    const sty = STATUS_BADGE[doc.status] || STATUS_BADGE.missing;
    const isPreviewing = previewing === doc.id;
    return (
      <div
        key={tid}
        className="border p-2.5 transition-card"
        style={{ borderColor: sty.border, backgroundColor: sty.bg }}
        data-testid={`admin-doc-${tid}`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold">{DOC_LABELS[doc.doc_type]}</span>
              <span className="label-mini" style={{ color: sty.text }}>{nested ? `RENEWAL · ${sty.label}` : sty.label}</span>
            </div>
            {doc.filename && (
              <div className="text-[11px] text-muted-foreground font-mono-tab truncate" title={doc.filename}>
                <FileText className="w-3 h-3 inline mr-1" />{doc.filename}
              </div>
            )}
            {doc.rejection_reason && (
              <div className="text-[11px] text-[#991B1B] mt-1">Note: {doc.rejection_reason}</div>
            )}
          </div>
          {doc.id && (
            <div className="flex gap-1 flex-shrink-0">
              <Button
                size="sm" variant="outline" onClick={() => openPreview(doc)}
                className="h-7 px-2 text-xs"
                data-testid={`preview-doc-${tid}`}
              >
                <Eye className="w-3 h-3 mr-1" /> View
              </Button>
              <Button
                size="sm" onClick={() => handleApprove(doc)}
                disabled={busyId === doc.id || doc.status === "approved"}
                className="h-7 px-2 text-xs bg-[#10B981] hover:bg-[#059669] text-white"
                data-testid={`approve-doc-${tid}`}
              >
                <CheckCircle2 className="w-3 h-3" />
              </Button>
              <Button
                size="sm" variant="outline"
                onClick={() => { setRejectingId(rejectingId === doc.id ? null : doc.id); setRejectReason(""); }}
                disabled={busyId === doc.id}
                className="h-7 px-2 text-xs border-[#EF4444] text-[#EF4444] hover:bg-[#FEF2F2]"
                data-testid={`reject-doc-${tid}`}
              >
                <XCircle className="w-3 h-3" />
              </Button>
            </div>
          )}
        </div>
        {/* Expiry date — admin records/clears it; drives reminders + compliance dashboard */}
        {doc.id && EXPIRING_TYPES.includes(doc.doc_type) && (() => {
          const draft = expiryDrafts[doc.id] ?? doc.expiry_date ?? "";
          const st = EXPIRY_STYLE[expiryStatus(doc.expiry_date).status];
          const issueDraft = issueDrafts[doc.id] ?? doc.issue_date ?? "";
          const expiryDirty = (expiryDrafts[doc.id] ?? doc.expiry_date ?? "") !== (doc.expiry_date ?? "");
          const issueDirty = doc.doc_type === "dbs" && issueDraft !== (doc.issue_date ?? "");
          const dirty = expiryDirty || issueDirty;
          const expiryLabel = doc.doc_type === "dbs" ? "RENEWAL" : "EXPIRY";
          return (
            <div className="mt-2 flex flex-wrap items-center gap-2" data-testid={`doc-expiry-${tid}`}>
              {doc.doc_type === "dbs" && (
                <>
                  <span className="label-mini text-muted-foreground inline-flex items-center gap-1">
                    <CalendarClock className="w-3 h-3" /> ISSUED
                  </span>
                  <input
                    type="date"
                    value={issueDraft}
                    onChange={(e) => setIssueDrafts((s) => ({ ...s, [doc.id]: e.target.value }))}
                    className="border border-border px-2 py-1 text-xs font-mono-tab focus:outline-none focus:border-[#0A0A0A]"
                    data-testid={`doc-issue-input-${tid}`}
                  />
                </>
              )}
              <span className="label-mini text-muted-foreground inline-flex items-center gap-1">
                <CalendarClock className="w-3 h-3" /> {expiryLabel}
              </span>
              <input
                type="date"
                value={draft}
                onChange={(e) => setExpiryDrafts((s) => ({ ...s, [doc.id]: e.target.value }))}
                className="border border-border px-2 py-1 text-xs font-mono-tab focus:outline-none focus:border-[#0A0A0A]"
                data-testid={`doc-expiry-input-${tid}`}
              />
              <span className="px-2 py-0.5 text-[10px] font-bold uppercase rounded-sm"
                style={{ color: st.fg, background: st.bg }}
                data-testid={`doc-expiry-status-${tid}`}>
                {st.label}
              </span>
              <Button
                size="sm"
                onClick={() => saveExpiry(doc)}
                disabled={savingExpiryId === doc.id || !dirty}
                className="h-7 px-2 text-xs bg-[#0A0A0A] hover:bg-[#1F1F1F] text-white"
                data-testid={`doc-expiry-save-${tid}`}
              >
                {savingExpiryId === doc.id ? "Saving…" : "Save"}
              </Button>
            </div>
          );
        })()}
        {allowUpload && !nested && (
          <div className="mt-2">
            <label
              className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 border border-[#002FA7] font-bold cursor-pointer transition-colors ${uploadingType === doc.doc_type ? "opacity-60 pointer-events-none" : "text-[#002FA7] hover:bg-[#002FA7] hover:text-white"}`}
              data-testid={`admin-upload-${tid}`}
            >
              {uploadingType === doc.doc_type ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              {uploadingType === doc.doc_type ? "Uploading…" : (doc.id ? "Replace document" : "Upload document")}
              <input
                type="file"
                accept="image/*,application/pdf"
                className="hidden"
                disabled={uploadingType === doc.doc_type}
                onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) handleUpload(doc.doc_type, f); }}
              />
            </label>
          </div>
        )}
        {doc.id && rejectingId === doc.id && (
          <div className="mt-2 space-y-2">
            <Label htmlFor={`docreason-${doc.id}`} className="label-mini">REJECTION REASON</Label>
            <Input
              id={`docreason-${doc.id}`}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. Image is blurry; please re-upload."
              maxLength={500}
              data-testid={`doc-reject-reason-${tid}`}
            />
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" size="sm" onClick={() => { setRejectingId(null); setRejectReason(""); }}>
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={() => handleReject(doc)}
                disabled={busyId === doc.id || rejectReason.trim().length < 2}
                className="bg-[#EF4444] hover:bg-[#DC2626] text-white"
                data-testid={`doc-reject-confirm-${tid}`}
              >
                Confirm
              </Button>
            </div>
          </div>
        )}
        {doc.id && isPreviewing && (
          <div className="mt-2 border border-border bg-white p-2 max-h-96 overflow-auto">
            {previewLoading && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="w-3 h-3 animate-spin" /> Loading preview…
              </div>
            )}
            {previewData && previewData.content_type?.startsWith("image/") && (
              <img
                src={`data:${previewData.content_type};base64,${previewData.data}`}
                alt={previewData.filename}
                className="max-w-full max-h-80 object-contain mx-auto"
                data-testid={`preview-image-${tid}`}
              />
            )}
            {previewData && previewData.content_type === "application/pdf" && (
              <embed
                src={`data:application/pdf;base64,${previewData.data}`}
                type="application/pdf"
                className="w-full h-80"
                data-testid={`preview-pdf-${tid}`}
              />
            )}
            <div className="flex justify-end mt-2">
              <Button variant="ghost" size="sm" onClick={closePreview} className="text-xs">Close preview</Button>
            </div>
          </div>
        )}
        {!nested && doc.renewal && (
          <div className="mt-2 pl-2 border-l-2 border-[#F59E0B]" data-testid={`admin-doc-renewal-block-${tid}`}>
            <div className="label-mini text-[#92400E] mb-1">RENEWAL SUBMITTED — CURRENT DOCUMENT STAYS ACTIVE UNTIL APPROVED</div>
            {renderCard(doc.renewal, `${doc.doc_type}-renewal`, true)}
          </div>
        )}
        {!nested && doc.history?.length > 0 && (
          <details className="mt-2 text-[11px]" data-testid={`doc-history-${tid}`}>
            <summary className="cursor-pointer text-muted-foreground">History ({doc.history.length})</summary>
            <ul className="mt-1 space-y-0.5">
              {doc.history.map((h) => (
                <li key={h.history_id} className="font-mono-tab" data-testid={`doc-history-item-${h.history_id}`}>
                  {(h.archived_at || "").slice(0, 10)} · {(STATUS_BADGE[h.status] || STATUS_BADGE.missing).label}
                  {h.expiry_date ? ` · exp ${h.expiry_date}` : ""}
                  {h.rejection_reason ? ` · "${h.rejection_reason}"` : ""}
                  {h.filename ? ` · ${h.filename}` : ""}
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
    );
  };

  return (
    <div className="mt-3 pt-3 border-t border-border" data-testid={`document-reviewer-${driverId}`}>
      {readiness && (
        <div
          className="mb-3 p-2.5 border"
          style={{
            borderColor: readiness.ready_for_approval ? "#10B981" : "#E5E7EB",
            backgroundColor: readiness.ready_for_approval ? "#ECFDF5" : "#F9FAFB",
          }}
          data-testid={`readiness-summary-${driverId}`}
        >
          <div className="flex items-center justify-between gap-3 mb-1.5">
            <span className="label-mini text-muted-foreground">APPROVAL READINESS</span>
            <span
              className="px-2 py-0.5 text-[10px] font-bold uppercase rounded-sm"
              style={{
                color: readiness.ready_for_approval ? "#065F46" : "#92400E",
                background: readiness.ready_for_approval ? "#D1FAE5" : "#FEF3C7",
              }}
              data-testid={`readiness-verdict-${driverId}`}
            >
              {readiness.approval_status === "approved"
                ? "APPROVED"
                : readiness.ready_for_approval
                  ? "READY FOR APPROVAL"
                  : `NOT READY · ${readiness.score}%`}
            </span>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {readiness.items.map((it) => (
              <span
                key={it.key}
                className="inline-flex items-center gap-1 text-[11px]"
                style={{ color: it.done ? "#065F46" : "#6B7280" }}
                data-testid={`readiness-item-${it.key}`}
              >
                {it.done ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                {it.label}
              </span>
            ))}
          </div>
        </div>
      )}
      <div className="label-mini text-muted-foreground mb-2">COMPLIANCE DOCUMENTS</div>
      {loading && <div className="text-xs text-muted-foreground">Loading documents…</div>}
      {!loading && (
        <div className="space-y-2">
          {ordered.map((doc) => renderCard(doc, doc.doc_type))}
        </div>
      )}
    </div>
  );
};

export default DocumentReviewer;
